// Player/AI intent enters the sim exclusively as Commands stamped with the tick
// they should apply on. A match is fully described by its seed + command log,
// which is what makes replays and the determinism test possible.

export type Command =
  | { t: "move"; ents: number[]; x: number; y: number; queue: boolean; attackMove: boolean }
  | { t: "attack"; ents: number[]; target: number; queue: boolean }
  | { t: "attackGround"; ents: number[]; x: number; y: number; queue: boolean }
  | { t: "stop"; ents: number[] }
  | { t: "build"; ents: number[]; defId: string; x: number; y: number; queue: boolean }
  | { t: "reclaim"; ents: number[]; target: number; queue: boolean }
  // Repair/resume: builders path to a FRIENDLY entity and apply build power to it.
  // If the target is still under construction this continues its construction
  // (draining metal+energy exactly like building); if the target is a completed
  // but damaged entity it restores health (energy only, no metal). One order kind
  // covers both so it adapts to the target's live state as the builder arrives.
  | { t: "repair"; ents: number[]; target: number; queue: boolean }
  | { t: "guard"; ents: number[]; target: number; queue: boolean }
  | { t: "factoryQueue"; factory: number; defId: string; count: number }
  | { t: "factoryCancel"; factory: number; defId: string }
  | { t: "factoryRally"; factory: number; x: number; y: number }
  | { t: "selfDestruct"; ents: number[] };

export interface TickCommand {
  tick: number;
  cmd: Command;
}

// Per-entity queued order (the sim's decomposition of a command).
export type Order =
  | { kind: "move"; x: number; y: number; attackMove: boolean }
  | { kind: "attack"; target: number }
  | { kind: "attackGround"; x: number; y: number }
  | { kind: "build"; defId: string; x: number; y: number; buildEnt: number }
  | { kind: "reclaim"; target: number }
  // Repair/resume an existing friendly entity (see the "repair" Command above).
  | { kind: "repair"; target: number }
  | { kind: "guard"; target: number };

/** A command log is just an ordered list of tick-stamped commands. */
export class CommandLog {
  entries: TickCommand[] = [];

  push(tick: number, cmd: Command): void {
    this.entries.push({ tick, cmd });
  }

  forTick(tick: number): Command[] {
    // entries are appended in tick order; linear scan is fine for slice scale.
    const out: Command[] = [];
    for (const e of this.entries) if (e.tick === tick) out.push(e.cmd);
    return out;
  }
}
